import React, { useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import PixelLoader from "../components/common/PixelLoader";
import PendingApprovalPage from "../modules/auth/PendingApprovalPage";
import authService from "../services/authService";

const PrivateRoute = ({ children, allowedRoles = [] }) => {
  const context  = useContext(AuthContext);
  const location = useLocation();

  // 🆕 فیکس بحرانی: قبلاً user.pending_role همیشه از localStorage/state
  // کش‌شده (که فقط موقع login نوشته می‌شد) خونده می‌شد — یعنی وقتی
  // ادمین pending_role کاربر رو تو سرور پاک می‌کرد (مثلاً با تأیید نقش
  // معلم)، خودِ کاربر تا وقتی دستی logout/login نمی‌کرد، همچنان تو
  // PendingApprovalPage گیر می‌کرد، چون فرانت هیچ‌وقت خودکار وضعیت رو
  // از سرور دوباره نمی‌گرفت. الان اگه pending_role تو کش پر باشه، قبل
  // از قطعی‌شدن تصمیم، یه‌بار مستقیم از /api/users/profile/ چک می‌شه —
  // اگه سرور می‌گه دیگه pending نیست، بلافاصله route واقعی رو نشون
  // می‌ده، بدون نیاز به logout/login دستی.
  const [rechecking, setRechecking] = useState(false);
  const [recheckedOnce, setRecheckedOnce] = useState(false);

  const { state, dispatch } = context || {};
  const { isAuthenticated, isLoading, user } = state || {};

  useEffect(() => {
    if (!isAuthenticated || !user?.pending_role || recheckedOnce) return;
    let cancelled = false;
    setRechecking(true);
    authService.getMe()
      .then(freshUser => {
        if (cancelled) return;
        localStorage.setItem("cached_user", JSON.stringify(freshUser));
        dispatch?.({ type: "AUTH_UPDATE_USER", payload: freshUser });
      })
      .catch(() => { /* silent — fallback to whatever we already have */ })
      .finally(() => { if (!cancelled) { setRechecking(false); setRecheckedOnce(true); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.pending_role, recheckedOnce]);

  // AuthContext not ready yet
  if (!context) return <PixelLoader variant="bar" text="بارگذاری..." fullPage />;

  // Still hydrating from localStorage
  if (isLoading) {
    return <PixelLoader variant="bar" text="در حال بارگذاری..." fullPage />;
  }

  // Not logged in → go to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 🆕 قبل از قطعی‌شدن تصمیمِ pending، صبر کن یه‌بار وضعیت از سرور
  // چک بشه (فقط همون بار اول که pending_role تو کش دیده می‌شه).
  if (user?.pending_role && !recheckedOnce) {
    return <PixelLoader variant="bar" text="در حال بررسی وضعیت حساب..." fullPage />;
  }

  if (user?.pending_role) {
    return <PendingApprovalPage />;
  }

  // Role check
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    const roleDashboards = {
      admin:   "/admin/dashboard",
      teacher: "/teacher/dashboard",
      support: "/support/dashboard",
      student: "/dashboard",
    };
    return <Navigate to={roleDashboards[user?.role] ?? "/dashboard"} replace />;
  }

  return children;
};

export default PrivateRoute;